/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import type { IUiThreeDPresentation, IPresentationChangeHandler } from './Interfaces';
import * as THREE from 'three';
import type { IRaycastable, Intersection, IViewPortController, ICenterLineViewModel } from '@/engine/app/interfaces';

import { reaction } from 'mobx';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { IPickablePolyLinePresentation } from './highlighters/PolyLineHighlighter';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { Line2 } from 'three/examples/jsm/lines/Line2';
import { AutoDisposer } from '@/engine/Utilities/AutoDisposer';
import type { IZoomFactorProvider } from '@/engine/app/modules/images/UIPresentation/ImagesPointPresentation';
import type { ImagesService } from '@/engine/app/modules/images/Images.service';
import type { SlicePositionInfo } from '@/engine/app/modules/images/ImageStackController';
import * as GeometryHelper from '@/engine/utils/geometryHelper';
import { shadeColor } from '@/engine/utils/colorUtils';

export class CenterlinePresentation implements IUiThreeDPresentation, IRaycastable, IPickablePolyLinePresentation {
  private _viewModel: ICenterLineViewModel;

  private _planningAlgorithm: PlanningAlgorithm;

  private readonly _name: string;

  private readonly _material: LineMaterial;

  private readonly _geometry: LineGeometry;

  private readonly _line: Line2;

  private readonly _color: string;

  private readonly _selectionColor: string;

  private readonly _object3D: THREE.Object3D;

  private _changeHandler: IPresentationChangeHandler;

  private _viewPortController: IViewPortController;

  private _subscriptions: DisposablesBasket = new DisposablesBasket();

  private _zoomFactorProvider: IZoomFactorProvider;

  private _imagesService: ImagesService;

  private _model: DT.Centerline | undefined;

  private readonly _sphereMesh: THREE.Mesh;

  private readonly _lineWidth = 2;

  private readonly _sphereSize = 1;

  private readonly _selectedLineWidth = 3;

  public constructor(
    planningAlgorithm: PlanningAlgorithm,
    viewModel: ICenterLineViewModel,
    changeHandler: IPresentationChangeHandler,
    viewPortController: IViewPortController,
    zoomFactorProvider: IZoomFactorProvider,
    imagesService: ImagesService
  ) {
    this._viewModel = viewModel;
    this._planningAlgorithm = planningAlgorithm;
    this._changeHandler = changeHandler;
    this._name = viewModel.descriptor;
    this._color = this._viewModel.color;
    this._selectionColor = this._viewModel.selectionColor;
    this._viewPortController = viewPortController;
    this._object3D = new THREE.Object3D();
    this._zoomFactorProvider = zoomFactorProvider;
    this._imagesService = imagesService;

    this._geometry = new LineGeometry();

    this._material = new LineMaterial({
      color: this._viewModel.isSelected
        ? new THREE.Color(this._selectionColor).getHex()
        : new THREE.Color(this._color).getHex(),
      linewidth: this._viewModel.isSelected ? this._selectedLineWidth : this._lineWidth,
      vertexColors: false,
      alphaToCoverage: true,
      transparent: true,
      opacity: 1,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide
    });

    this._line = new Line2(this._geometry, this._material);
    this._line.visible = this._viewModel.isVisible;

    this._model = this._planningAlgorithm.getValue(this._name, DT.Centerline);

    if (this._model && this._model.value.points.length > 0) {
      const positions: Array<number> = [];
      for (const point of this._model.value.points) {
        positions.push(point.x, point.y, point.z);
      }
      this._geometry.setPositions(positions);
      this._line.computeLineDistances();
    }

    this._line.scale.set(1, 1, 1);
    this._line.renderOrder = 1;

    const currentVpSize = this._viewPortController.getSize();
    this._material.resolution.set(currentVpSize.x, currentVpSize.y);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this._object3D.add(this._line);

    const sphereGeometry = new THREE.SphereGeometry(this._sphereSize);
    const sphereMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color(shadeColor(this._selectionColor, -20)),
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 1
    });

    this._sphereMesh = new THREE.Mesh(sphereGeometry, sphereMaterial);
    this._sphereMesh.renderOrder = 2;
    this._sphereMesh.visible = this._viewModel.isSelected;
    this._object3D.add(this._sphereMesh);

    this._subscriptions.add(
      this._planningAlgorithm.addOnChangedCallbackFor<DT.Centerline>(this._name, this._onGeometryChanged.bind(this))
    ); // TODO: inline binding is an issue

    this._subscriptions.add(
      new AutoDisposer(
        reaction(
          () => viewModel.isHighlighted,
          (highlighting) => {
            const color = highlighting
              ? new THREE.Color(this._color)
              : this._viewModel.isSelected
                ? new THREE.Color(this._selectionColor)
                : new THREE.Color(this._color);
            this._line.material.linewidth = highlighting
              ? this._selectedLineWidth
              : this._viewModel.isSelected
                ? this._selectedLineWidth
                : this._lineWidth;
            this._line.material.color = color;
            this._changeHandler.handlePresentationChange();
          }
        )
      )
    );

    this._subscriptions.add(
      new AutoDisposer(
        reaction(
          () => viewModel.isSelected,
          (selection) => {
            this._line.material.linewidth = selection ? this._selectedLineWidth : this._lineWidth;
            this._line.material.color = selection
              ? new THREE.Color(this._selectionColor)
              : new THREE.Color(this._color);
            this._sphereMesh.visible = this._viewModel.isVisible && selection;
            this._changeHandler.handlePresentationChange();
          }
        )
      )
    );

    this._subscriptions.add(
      new AutoDisposer(
        reaction(
          () => viewModel.isVisible,
          (visibility) => {
            this._line.visible = visibility;
            this._sphereMesh.visible = visibility && this._viewModel.isSelected;
            this._changeHandler.handlePresentationChange();
          }
        )
      )
    );

    this._subscriptions.add(
      this._imagesService.subscribeToSlicePositionChange(
        this._viewModel.alongSliceDescriptor,
        this._handleSlicePositionChange.bind(this)
      )
    );
  }

  private _onGeometryChanged(model?: DT.Centerline): void {
    if (model && model.value.points.length > 0) {
      this._model = model;
      const positions: Array<number> = [];
      for (const point of model.value.points) {
        positions.push(point.x, point.y, point.z);
      }
      this._geometry.setPositions(positions);
      this._line.computeLineDistances();
    }
    this._changeHandler.handlePresentationChange();
  }

  private _handleSlicePositionChange(slicePosition: SlicePositionInfo): void {
    const slicePoint = this._model?.value.points.find((point: THREE.Vector3) => {
      return GeometryHelper.isPointNearPlane(point, slicePosition.sliceCS, 0.1);
    });
    if (slicePoint) {
      this._sphereMesh.position.set(slicePoint.x, slicePoint.y, slicePoint.z);
    }
    this._changeHandler.handlePresentationChange();
  }

  public get3dObject(): THREE.Object3D {
    return this._object3D;
  }

  public getPickableLinesCoords(): Array<[THREE.Vector3, THREE.Vector3]> {
    const linesCoords: Array<[THREE.Vector3, THREE.Vector3]> = [];
    if (!this._model) {
      return linesCoords;
    }
    for (let i = 0; i < this._model.value.points.length - 1; i++) {
      const pointFrom = this._model.value.points[i];
      const pointTo = this._model.value.points[i + 1];
      linesCoords.push([pointFrom, pointTo]);
    }
    return linesCoords;
  }

  public rayCast(/*origin: THREE.Vector3, direction: THREE.Vector3*/): Array<Intersection> {
    return [];
  }

  public get lineWidth(): number {
    const currentWidth = this._viewModel.isSelected ? this._selectedLineWidth : this._lineWidth;
    return currentWidth * this._zoomFactorProvider.getCameraZoomFactor();
  }

  public dispose(): void {
    this._subscriptions.dispose();
    this._geometry.dispose();
    this._material.dispose();
  }
}
